import {
	MutationKey,
	Query,
	QueryFunction,
	QueryKey,
	useMutation as useReactMutation,
	useQuery as useReactQuery,
} from '@tanstack/react-query';

import { auth } from '@store/selectors';
import { useAppSelector } from '@store/store';

import { handleErrorFromMessage } from '@utils/handleRequestError';

import { IResponseError } from '@interfaces/errors/apiErrors';

import { EErrorCodes, PermissionErrorCause } from '@enums/errorCodes';

import keycloakInstance from '../keycloak';

const NODE_ENV = process.env.NODE_ENV;

const QUERY_RETRY_COUNT = 1;
const QUERY_CACHE_TIME = 10000;
const ALL_DATA_STALE_TIME = 5 * 60 * 1000;

interface IUseReactQueryParams<TFnData = any, TData = any> {
	queryKey: QueryKey;
	queryFn: QueryFunction<TFnData, any>;
	select?: (data: TFnData) => TData;
	onSuccess?: ((data: TData) => unknown) | undefined;
	allowedRoles?: string[];
	requiredRoles?: string[];
	restrictedRoles?: string[];
	fetchAllData?: boolean;
	skipOrgContext?: boolean;
	refetchInterval?:
		number | false |
		((data: TData | undefined, query: Query<TFnData, any, TFnData, any>) => number | false) | undefined;

	[x: string]: any;
}

interface IUseReactMutationParams<TData = any, TVariables = any, TContext = any> {
	mutationKey: MutationKey;
	mutationFn: any;
	allowedRoles?: string[];
	requiredRoles?: string[];
	restrictedRoles?: string[];
	onSuccess?: ((data: TData, variables: TVariables, context: TContext | undefined) => unknown) | undefined;
	onError?: any;

	[x: string]: any;
}

const logoutOnExpiredToken = (err: IResponseError) => {
	const isExpiredToken = !!err?.errors?.find((item) => item.code === EErrorCodes.unauthorized);

	if (isExpiredToken) {
		void keycloakInstance.logout();
	}
};

const useQuery = <TFnData = any, TData = TFnData, TError extends IResponseError = any>({
	queryKey,
	queryFn,
	select,
	allowedRoles = [],
	requiredRoles = [],
	restrictedRoles = [],
	fetchAllData = false,
	skipOrgContext = false,
	refetchInterval = false,
	...rest
}: IUseReactQueryParams<TFnData, TData>): {
	data: TData | undefined;
	allData?: TData | undefined;
	error: TError | null;
	isFetching: boolean;
	status: 'loading' | 'error' | 'success';
	isLoading: boolean;
	refetch: any;
	isInitialLoading: boolean;
	isSuccess: boolean;
	isError: boolean;
} => {
	const { enabled = [], ...options } = rest;

	const {
		user,
		activeOrganisation,
		accessPermission: { platformOwnerAdministrator },
	} = useAppSelector(auth);

	const shouldInjectOrgContext = !skipOrgContext && !platformOwnerAdministrator && activeOrganisation;

	const contextualQueryKey = shouldInjectOrgContext
		? [...queryKey, { __orgContext: activeOrganisation }]
		: queryKey;

	const userRoles = user?.roles || [];

	const hasAllowedRoles = allowedRoles.length === 0 || allowedRoles.some((role) => userRoles.includes(role));
	const hasRequiredRoles = (
		requiredRoles.length === 0 ||
		requiredRoles.every((role) => userRoles.includes(role))
	);
	const hasRestrictedRoles = restrictedRoles.some((role) => userRoles.includes(role));
	const hasEnabledParams = enabled.length === 0 || enabled.every((item: boolean) => item);
	const isEnvironmentTest = NODE_ENV === 'test';
	const hasToken = !!sessionStorage.getItem('token');

	const isEnabled = (
		hasEnabledParams &&
		(isEnvironmentTest || hasToken) &&
		hasAllowedRoles &&
		hasRequiredRoles &&
		!hasRestrictedRoles
	);

	const {
		data,
		error,
		isFetching,
		status,
		isLoading,
		refetch,
		isInitialLoading,
		isSuccess,
		isError,
	} = useReactQuery<TFnData, TError, TData, any>(contextualQueryKey, queryFn, {
		retry: QUERY_RETRY_COUNT,
		select,
		keepPreviousData: true,
		onError: (err) => {
			logoutOnExpiredToken(err);

			handleErrorFromMessage(err);
		},
		enabled: isEnabled,
		refetchOnWindowFocus: false,
		cacheTime: QUERY_CACHE_TIME,
		...options,
		refetchInterval,
	});

	const count = (data as any)?.count;
	const allDataQueryKey = shouldInjectOrgContext
		? [...queryKey, 'all-data', count, { __orgContext: activeOrganisation }]
		: [...queryKey, 'all-data', count];
	const hasCountProperty = data && typeof data === 'object' && 'count' in data;
	const shouldFetchAllData = count ? fetchAllData && isEnabled && hasCountProperty : false;

	const allDataParams = shouldFetchAllData ? (() => {
		const [, params] = queryKey as [string, any];

		return {
			...params,
			page: 1,
			pageSize: count,
		};
	})() : null;

	const {
		data: allData,
	} = useReactQuery<TFnData, TError, TData, any>(
		allDataQueryKey,
		allDataParams
			? ((context) => queryFn({ ...context, queryKey: [queryKey[0], allDataParams] }))
			: (() => Promise.resolve(null as any)),
		{
			retry: QUERY_RETRY_COUNT,
			select,
			keepPreviousData: true,
			onError: (err) => {
				logoutOnExpiredToken(err);
				handleErrorFromMessage(err);
			},
			enabled: !!allDataParams && isEnabled,
			refetchOnWindowFocus: false,
			cacheTime: QUERY_CACHE_TIME,
			staleTime: ALL_DATA_STALE_TIME,
			...options,
		},
	);

	return {
		data,
		allData: fetchAllData ? allData : undefined,
		error,
		isFetching,
		status,
		isLoading,
		refetch,
		isInitialLoading,
		isSuccess,
		isError,
	};
};

const useMutation = <
	TVariables = void,
	TData = any,
	TError extends IResponseError = any,
	TContext = any
>({
	mutationKey,
	mutationFn,
	onSuccess,
	onError,
	allowedRoles = [],
	requiredRoles = [],
	restrictedRoles = [],
	enabled = [],
	...rest
}: IUseReactMutationParams<TData, TVariables, TContext>) => {
	const {
		user,
	} = useAppSelector(auth);

	const userRoles = user?.roles || [];

	const hasAllowedRoles = allowedRoles.length === 0 || allowedRoles.some((role) => userRoles.includes(role));
	const hasRequiredRoles = (
		requiredRoles.length === 0 ||
		requiredRoles.every((role) => userRoles.includes(role))
	);
	const hasRestrictedRoles = restrictedRoles.some((role) => userRoles.includes(role));
	const hasEnabledParams = enabled.length === 0 || enabled.every((item: boolean) => item);
	const isEnvironmentTest = NODE_ENV === 'test';
	const hasToken = !!sessionStorage.getItem('token');

	const isEnabled = (
		hasEnabledParams &&
		(isEnvironmentTest || hasToken) &&
		hasAllowedRoles &&
		hasRequiredRoles &&
		!hasRestrictedRoles
	);

	const wrappedMutationFn = async (variables: TVariables) => {
		if (!isEnabled) {
			return Promise.reject(
				new Error(EErrorCodes.permissionError, { cause: PermissionErrorCause as unknown as Error }),
			);
		}

		return mutationFn(variables);
	};

	const {
		data,
		error,
		isError,
		isLoading,
		isSuccess,
		mutate,
		mutateAsync,
		status,
		reset,
	} = useReactMutation<TData, TError, TVariables, TContext>(wrappedMutationFn, {
		mutationKey,
		onSuccess,
		onError: (err) => {
			logoutOnExpiredToken(err);

			if (onError) onError(err);
			else handleErrorFromMessage(err);
		},
		...rest,
	});

	return {
		data,
		error,
		isError,
		isSuccess,
		isLoading,
		status,
		mutate,
		mutateAsync,
		reset,
	};
};

export {
	useMutation,
	useQuery,
};
